import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import AccountDropdown from './AccountDropdown';
import ChatPanel from './ChatPanel';
import { apiFetch } from './api';
import './ContractsPage.css';

// ── Shared form helpers ───────────────────────────────────────────────────────

function Field({ label, hint, children }) {
  return (
    <div className="mb-3">
      <label className="form-label fw-semibold">{label}</label>
      {children}
      {hint && <div className="form-text" style={{ fontSize: '0.8rem' }}>{hint}</div>}
    </div>
  );
}

function SectionHeading({ children }) {
  return <h6 className="ct-section-heading">{children}</h6>;
}

// ── Type-specific contract field sections ─────────────────────────────────────

function TcocContractFields({ form, setForm }) {
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <>
      <SectionHeading>Financial Terms</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Benchmark PMPM ($)" hint="Per-member per-month cost target">
            <input className="form-control" type="number" min="0" step="0.01"
              value={form.benchmarkPmpm ?? ''} onChange={set('benchmarkPmpm')} placeholder="e.g. 850.00" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Minimum Savings Rate (MSR%)" hint="Threshold before savings sharing begins">
            <input className="form-control" type="number" min="0" max="100" step="0.1"
              value={form.msrPct ?? ''} onChange={set('msrPct')} placeholder="e.g. 2.0" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Shared Savings Rate (%)">
            <input className="form-control" type="number" min="0" max="100" step="0.1"
              value={form.sharedSavingsPct ?? ''} onChange={set('sharedSavingsPct')} placeholder="e.g. 50.0" />
          </Field>
        </div>
      </div>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Risk Arrangement">
            <select className="form-select" value={form.riskArrangement ?? 'One-Sided'} onChange={set('riskArrangement')}>
              <option>One-Sided</option>
              <option>Two-Sided</option>
            </select>
          </Field>
        </div>
        {form.riskArrangement === 'Two-Sided' && (
          <div className="col-md-4">
            <Field label="Shared Losses Rate (%)">
              <input className="form-control" type="number" min="0" max="100" step="0.1"
                value={form.sharedLossesPct ?? ''} onChange={set('sharedLossesPct')} placeholder="e.g. 30.0" />
            </Field>
          </div>
        )}
        <div className="col-md-4">
          <Field label="Quality Withhold (%)">
            <input className="form-control" type="number" min="0" max="100" step="0.1"
              value={form.qualityWithholdPct ?? ''} onChange={set('qualityWithholdPct')} placeholder="e.g. 5.0" />
          </Field>
        </div>
      </div>

      <SectionHeading>Risk Corridors</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Corridor Type">
            <select className="form-select" value={form.corridorType ?? 'Symmetric'} onChange={set('corridorType')}>
              <option>Symmetric</option>
              <option>Asymmetric</option>
            </select>
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Upper Corridor Limit (%)" hint="Savings above this % are capped">
            <input className="form-control" type="number" min="0" max="100" step="0.1"
              value={form.corridorUpperPct ?? ''} onChange={set('corridorUpperPct')} placeholder="e.g. 10.0" />
          </Field>
        </div>
        {form.corridorType === 'Asymmetric' && (
          <div className="col-md-4">
            <Field label="Lower Corridor Limit (%)" hint="Losses above this % shift to payer">
              <input className="form-control" type="number" min="0" max="100" step="0.1"
                value={form.corridorLowerPct ?? ''} onChange={set('corridorLowerPct')} placeholder="e.g. 8.0" />
            </Field>
          </div>
        )}
      </div>

      <SectionHeading>Performance &amp; Settlement</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Quality Score Threshold (%)" hint="Minimum to qualify for shared savings">
            <input className="form-control" type="number" min="0" max="100" step="0.1"
              value={form.qualityThresholdPct ?? ''} onChange={set('qualityThresholdPct')} placeholder="e.g. 70.0" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Settlement Frequency">
            <select className="form-select" value={form.settlementFrequency ?? 'Annual'} onChange={set('settlementFrequency')}>
              <option>Annual</option>
              <option>Semi-Annual</option>
              <option>Quarterly</option>
            </select>
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Performance Period">
            <select className="form-select" value={form.performancePeriod ?? 'Calendar Year'} onChange={set('performancePeriod')}>
              <option>Calendar Year</option>
              <option>Rolling 12-Month</option>
              <option>Custom</option>
            </select>
          </Field>
        </div>
      </div>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Estimated Attribution Count" hint="Approximate attributed members">
            <input className="form-control" type="number" min="0"
              value={form.attributionCount ?? ''} onChange={set('attributionCount')} placeholder="e.g. 5000" />
          </Field>
        </div>
      </div>
    </>
  );
}

function CapitationContractFields({ form, setForm }) {
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <>
      <SectionHeading>Rate Structure</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="PMPM Rate ($)" hint="Per-member per-month capitation payment">
            <input className="form-control" type="number" min="0" step="0.01"
              value={form.pmpmRate ?? ''} onChange={set('pmpmRate')} placeholder="e.g. 450.00" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Rate Effective Date">
            <input className="form-control" type="date"
              value={form.rateEffectiveDate ?? ''} onChange={set('rateEffectiveDate')} />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Annual Rate Escalator (%)" hint="Year-over-year PMPM increase">
            <input className="form-control" type="number" min="0" max="20" step="0.1"
              value={form.rateEscalatorPct ?? ''} onChange={set('rateEscalatorPct')} placeholder="e.g. 3.5" />
          </Field>
        </div>
      </div>

      <SectionHeading>Risk Corridors &amp; Adjustment</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Lower Corridor Bandwidth (%)" hint="Corridor below PMPM before shared risk">
            <input className="form-control" type="number" min="0" max="30" step="0.1"
              value={form.corridorLowerPct ?? ''} onChange={set('corridorLowerPct')} placeholder="e.g. 5.0" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Upper Corridor Bandwidth (%)" hint="Corridor above PMPM before risk protection">
            <input className="form-control" type="number" min="0" max="30" step="0.1"
              value={form.corridorUpperPct ?? ''} onChange={set('corridorUpperPct')} placeholder="e.g. 5.0" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Risk Adjustment Methodology">
            <select className="form-select" value={form.riskAdjMethod ?? 'CMS-HCC'} onChange={set('riskAdjMethod')}>
              <option>CMS-HCC</option>
              <option>Johns Hopkins ACG</option>
              <option>3M CRG</option>
              <option>Custom</option>
              <option>None</option>
            </select>
          </Field>
        </div>
      </div>

      <SectionHeading>Quality Incentives &amp; Settlement</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Quality Withhold (%)">
            <input className="form-control" type="number" min="0" max="100" step="0.1"
              value={form.qualityWithholdPct ?? ''} onChange={set('qualityWithholdPct')} placeholder="e.g. 5.0" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Quality Score Threshold (%)">
            <input className="form-control" type="number" min="0" max="100" step="0.1"
              value={form.qualityThresholdPct ?? ''} onChange={set('qualityThresholdPct')} placeholder="e.g. 65.0" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Settlement Frequency">
            <select className="form-select" value={form.settlementFrequency ?? 'Monthly'} onChange={set('settlementFrequency')}>
              <option>Monthly</option>
              <option>Quarterly</option>
              <option>Annual</option>
            </select>
          </Field>
        </div>
      </div>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Enrollment Minimum" hint="Minimum enrolled members to activate rate">
            <input className="form-control" type="number" min="0"
              value={form.enrollmentMin ?? ''} onChange={set('enrollmentMin')} placeholder="e.g. 500" />
          </Field>
        </div>
      </div>
    </>
  );
}

function PmpmContractFields({ form, setForm }) {
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <>
      <SectionHeading>Condition PMPM</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Condition PMPM Rate ($)" hint="Per-member per-month payment for attributed condition population">
            <input className="form-control" type="number" min="0" step="0.01"
              value={form.conditionPmpmRate ?? ''} onChange={set('conditionPmpmRate')} placeholder="e.g. 120.00" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Risk Corridor — Lower Bound (%)" hint="Below which payer recaptures savings">
            <input className="form-control" type="number" min="0" max="50" step="0.1"
              value={form.corridorLowerPct ?? ''} onChange={set('corridorLowerPct')} placeholder="e.g. 10.0" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Risk Corridor — Upper Bound (%)" hint="Above which provider is protected">
            <input className="form-control" type="number" min="0" max="50" step="0.1"
              value={form.corridorUpperPct ?? ''} onChange={set('corridorUpperPct')} placeholder="e.g. 10.0" />
          </Field>
        </div>
      </div>

      <SectionHeading>Quality Gates</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Quality Gate Score (%)" hint="Minimum composite score to receive full PMPM">
            <input className="form-control" type="number" min="0" max="100" step="0.1"
              value={form.qualityGateScore ?? ''} onChange={set('qualityGateScore')} placeholder="e.g. 60.0" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Measure Threshold Count" hint="Minimum number of measures provider must pass">
            <input className="form-control" type="number" min="0"
              value={form.measureThresholdCount ?? ''} onChange={set('measureThresholdCount')} placeholder="e.g. 4" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Quality Withhold (%)">
            <input className="form-control" type="number" min="0" max="30" step="0.1"
              value={form.qualityWithholdPct ?? ''} onChange={set('qualityWithholdPct')} placeholder="e.g. 10.0" />
          </Field>
        </div>
      </div>

      <SectionHeading>Terms &amp; Settlement</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Outlier Threshold ($)" hint="Claims above this amount excluded from attribution">
            <input className="form-control" type="number" min="0"
              value={form.outlierThreshold ?? ''} onChange={set('outlierThreshold')} placeholder="e.g. 100000" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Reconciliation Frequency">
            <select className="form-select" value={form.reconciliationFreq ?? 'Quarterly'} onChange={set('reconciliationFreq')}>
              <option>Quarterly</option>
              <option>Semi-Annual</option>
              <option>Annual</option>
            </select>
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Enrollment Minimum">
            <input className="form-control" type="number" min="0"
              value={form.enrollmentMin ?? ''} onChange={set('enrollmentMin')} placeholder="e.g. 100" />
          </Field>
        </div>
      </div>
    </>
  );
}

function BundlingContractFields({ form, setForm }) {
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <>
      <SectionHeading>Bundle Pricing</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Target Bundle Price ($)" hint="All-inclusive episode target payment">
            <input className="form-control" type="number" min="0" step="0.01"
              value={form.targetBundlePrice ?? ''} onChange={set('targetBundlePrice')} placeholder="e.g. 28500.00" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Gainsharing Rate (%)" hint="Provider share of savings below target">
            <input className="form-control" type="number" min="0" max="100" step="0.1"
              value={form.gainsharingPct ?? ''} onChange={set('gainsharingPct')} placeholder="e.g. 50.0" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Downside Risk Rate (%)" hint="Provider liability if costs exceed target">
            <input className="form-control" type="number" min="0" max="100" step="0.1"
              value={form.downsideRiskPct ?? ''} onChange={set('downsideRiskPct')} placeholder="e.g. 20.0" />
          </Field>
        </div>
      </div>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Stop-Loss Threshold ($)" hint="Maximum provider exposure per episode">
            <input className="form-control" type="number" min="0"
              value={form.stopLossThreshold ?? ''} onChange={set('stopLossThreshold')} placeholder="e.g. 5000" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Annual Target Price Adjustment (%)" hint="Year-over-year target price trend">
            <input className="form-control" type="number" min="-20" max="20" step="0.1"
              value={form.targetPriceAdjPct ?? ''} onChange={set('targetPriceAdjPct')} placeholder="e.g. -2.5" />
          </Field>
        </div>
      </div>

      <SectionHeading>Quality &amp; Compliance</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Quality Adjustment Withhold (%)">
            <input className="form-control" type="number" min="0" max="30" step="0.1"
              value={form.qualityWithholdPct ?? ''} onChange={set('qualityWithholdPct')} placeholder="e.g. 5.0" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Quality Score Threshold (%)">
            <input className="form-control" type="number" min="0" max="100" step="0.1"
              value={form.qualityThresholdPct ?? ''} onChange={set('qualityThresholdPct')} placeholder="e.g. 70.0" />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Minimum Episode Volume" hint="Minimum episodes before settlement applies">
            <input className="form-control" type="number" min="0"
              value={form.minEpisodeVolume ?? ''} onChange={set('minEpisodeVolume')} placeholder="e.g. 20" />
          </Field>
        </div>
      </div>

      <SectionHeading>Settlement &amp; Outliers</SectionHeading>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Reconciliation Frequency">
            <select className="form-select" value={form.reconciliationFreq ?? 'Semi-Annual'} onChange={set('reconciliationFreq')}>
              <option>Quarterly</option>
              <option>Semi-Annual</option>
              <option>Annual</option>
            </select>
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Outlier Policy">
            <select className="form-select" value={form.outlierPolicy ?? 'Fixed Threshold'} onChange={set('outlierPolicy')}>
              <option>Fixed Threshold</option>
              <option>Exclude Top Percentile</option>
              <option>No Outlier Exclusion</option>
            </select>
          </Field>
        </div>
        {form.outlierPolicy !== 'No Outlier Exclusion' && (
          <div className="col-md-4">
            <Field
              label={form.outlierPolicy === 'Exclude Top Percentile' ? 'Outlier Percentile (%)' : 'Outlier Threshold ($)'}
              hint={form.outlierPolicy === 'Exclude Top Percentile' ? 'Top % of episodes excluded' : 'Episodes above this cost excluded'}
            >
              <input className="form-control" type="number" min="0"
                value={form.outlierValue ?? ''} onChange={set('outlierValue')} />
            </Field>
          </div>
        )}
      </div>
    </>
  );
}

const TYPE_CONTRACT_FIELDS = {
  'Total Cost of Care': TcocContractFields,
  'Specialty Capitation / Sub-Capitation': CapitationContractFields,
  'Condition Specific PMPM': PmpmContractFields,
  'Bundling': BundlingContractFields,
};

const STATUS_COLORS = {
  'Draft': '#6c757d',
  'Active': '#198754',
  'Expired': '#dc3545',
  'Terminated': '#dc3545',
  'Pending Renewal': '#fd7e14',
};

// ── Page shell ────────────────────────────────────────────────────────────────

export default function ContractsPage({ onNavigate, currentUser, onLogout, chatStateRef }) {
  const [programs, setPrograms] = useState([]);
  const [contracts, setContracts] = useState([]);
  const contractsRef = useRef([]);
  const programsRef = useRef([]);
  useEffect(() => { contractsRef.current = contracts; }, [contracts]);
  useEffect(() => { programsRef.current = programs; }, [programs]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [expandedPrograms, setExpandedPrograms] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // AI panel
  const aiPanelRef = useRef(null);
  const [aiCollapsed, setAiCollapsed] = useState(false);
  const toggleAi = useCallback(() => {
    if (aiCollapsed) aiPanelRef.current?.expand();
    else aiPanelRef.current?.collapse();
  }, [aiCollapsed]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [progRes, ctRes] = await Promise.all([
        apiFetch('/api/vbp/programs'),
        apiFetch('/api/contracts'),
      ]);
      if (!progRes.ok || !ctRes.ok) throw new Error('Failed to fetch');
      const [progs, cts] = await Promise.all([progRes.json(), ctRes.json()]);
      setPrograms(progs);
      setContracts(cts.map(c => ({ ...c, ...c.typeData })));
      // Auto-expand programs that have contracts
      setExpandedPrograms(prev => {
        const next = new Set(prev);
        cts.forEach(c => next.add(c.programId));
        return next;
      });
    } catch (e) {
      setApiError('Failed to load data. ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Open the correct contract from URL on first load
  const initializedFromURL = useRef(false);
  useEffect(() => {
    if (loading || initializedFromURL.current) return;
    initializedFromURL.current = true;
    const id = new URLSearchParams(window.location.search).get('id');
    if (id && id !== 'new') {
      const c = contractsRef.current.find(x => x.id === id);
      if (c) {
        const prog = programsRef.current.find(p => p.id === c.programId);
        setDraft({ ...c, _programType: prog?.programType ?? c.programType ?? '' });
        setEditingId(c.id);
        setExpandedPrograms(prev => new Set([...prev, c.programId]));
      } else {
        history.replaceState(null, '', '/contracts');
      }
    }
  }, [loading]);

  // Handle browser back / forward
  useEffect(() => {
    const handler = () => {
      const id = new URLSearchParams(window.location.search).get('id');
      if (!id) {
        setEditingId(null); setDraft(null); setDeleteConfirm(false); setApiError(null);
      } else if (id === 'new') {
        history.replaceState(null, '', '/contracts');
        setEditingId(null); setDraft(null);
      } else {
        const c = contractsRef.current.find(x => x.id === id);
        if (c) {
          const prog = programsRef.current.find(p => p.id === c.programId);
          setDraft({ ...c, _programType: prog?.programType ?? c.programType ?? '' });
          setEditingId(c.id); setSaved(false); setApiError(null); setDeleteConfirm(false);
          setExpandedPrograms(prev => new Set([...prev, c.programId]));
        } else {
          history.replaceState(null, '', '/contracts');
          setEditingId(null); setDraft(null);
        }
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  const toggleExpand = (programId) => {
    setExpandedPrograms(prev => {
      const next = new Set(prev);
      if (next.has(programId)) next.delete(programId);
      else next.add(programId);
      return next;
    });
  };

  const startNew = (programId) => {
    const prog = programs.find(p => p.id === programId);
    setDraft({
      programId,
      name: '',
      contractYear: '2026',
      status: 'Draft',
      effectiveDate: '',
      terminationDate: '',
      payer: '',
      providerOrg: '',
      contractRef: '',
      _programType: prog?.programType ?? '',
    });
    setEditingId('new');
    setSaved(false);
    setApiError(null);
    setDeleteConfirm(false);
    setExpandedPrograms(prev => new Set([...prev, programId]));
    history.pushState(null, '', '/contracts?id=new');
  };

  const startEdit = (contract) => {
    const prog = programs.find(p => p.id === contract.programId);
    setDraft({ ...contract, _programType: prog?.programType ?? contract.programType ?? '' });
    setEditingId(contract.id);
    setSaved(false);
    setApiError(null);
    setDeleteConfirm(false);
    history.pushState(null, '', '/contracts?id=' + contract.id);
  };

  const cancel = () => {
    history.pushState(null, '', '/contracts');
    setEditingId(null);
    setDraft(null);
    setDeleteConfirm(false);
    setApiError(null);
  };

  const buildPayload = () => {
    const { programId, name, contractYear, status, effectiveDate, terminationDate,
      payer, providerOrg, contractRef, id, _programType, programName, programType, ...typeData } = draft;
    return { programId, name, contractYear, status, effectiveDate, terminationDate,
      payer, providerOrg, contractRef, typeData };
  };

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    setApiError(null);
    try {
      let res;
      if (editingId === 'new') {
        res = await apiFetch('/api/contracts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildPayload()),
        });
      } else {
        res = await apiFetch(`/api/contracts/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildPayload()),
        });
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const savedContract = await res.json();
      const newId = savedContract.id;
      if (editingId === 'new') {
        history.replaceState(null, '', '/contracts?id=' + newId);
        setEditingId(newId);
      }
      await loadData();
      setSaved(true);
      setTimeout(() => setSaved(false), 3500);
    } catch (e) {
      setApiError('Failed to save: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await apiFetch(`/api/contracts/${editingId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadData();
      history.pushState(null, '', '/contracts');
      cancel();
    } catch (e) {
      setApiError('Failed to delete: ' + e.message);
    }
  };

  const TypeFields = draft?._programType ? TYPE_CONTRACT_FIELDS[draft._programType] : null;

  // Filter + group contracts by program
  const filteredContracts = contracts.filter(c =>
    (statusFilter === 'All' || c.status === statusFilter) &&
    (searchQuery === '' || (c.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const contractsByProgram = filteredContracts.reduce((acc, c) => {
    if (!acc[c.programId]) acc[c.programId] = [];
    acc[c.programId].push(c);
    return acc;
  }, {});

  const ALL_STATUSES = ['All', 'Draft', 'Active', 'Pending Renewal', 'Expired', 'Terminated'];

  return (
    <div className="d-flex flex-column" style={{ height: '100vh', background: 'var(--eh-navy)' }}>
      <nav className="navbar navbar-dark bg-primary px-4 py-2 flex-shrink-0">
        <span
          className="navbar-brand mb-0 d-flex align-items-center gap-3"
          style={{ cursor: 'pointer' }}
          onClick={() => onNavigate('home')}
        >
          <img src="/enlace-logo-white.svg" alt="Enlace Health" style={{ height: 36 }} />
          <span style={{ fontWeight: 300, opacity: 0.65, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#fff' }}>
            Contracts
          </span>
        </span>
        {currentUser && (
          <AccountDropdown currentUser={currentUser} onLogout={onLogout} />
        )}
      </nav>

      <div className="d-flex flex-grow-1 overflow-hidden">
        {/* ── Sidebar ── */}
        <div className="ct-sidebar">
          <div className="ct-sidebar-title">Contracts</div>
          <div className="ct-search-bar">
            <input
              type="search"
              className="ct-search-input"
              placeholder="Search contracts…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="ct-filter-bar">
            {ALL_STATUSES.map(s => (
              <button
                key={s}
                className={`ct-filter-chip${statusFilter === s ? ' active' : ''}`}
                onClick={() => setStatusFilter(s)}
                style={s !== 'All' && s !== statusFilter
                  ? { borderColor: (STATUS_COLORS[s] ?? '#6c757d') + '66' }
                  : s !== 'All' && statusFilter === s
                    ? { background: STATUS_COLORS[s] ?? '#6c757d', borderColor: STATUS_COLORS[s] ?? '#6c757d' }
                    : {}}
              >
                {s === 'All'
                  ? `All (${contracts.length})`
                  : `${s} (${contracts.filter(c => c.status === s).length})`
                }
              </button>
            ))}
          </div>
          {loading ? null : programs.length === 0 ? (
            <div className="ct-sidebar-empty">No programs defined yet</div>
          ) : (
            programs.map(prog => {
              const progContracts = contractsByProgram[prog.id] || [];
              const expanded = expandedPrograms.has(prog.id);
              return (
                <div key={prog.id} className="ct-program-group">
                  <button
                    className="ct-program-header"
                    onClick={() => toggleExpand(prog.id)}
                  >
                    <span className="ct-program-expand">{expanded ? '▾' : '▸'}</span>
                    <span className="ct-program-header-inner">
                      <span className="ct-program-header-name">{prog.name || <em style={{ opacity: 0.6 }}>Untitled</em>}</span>
                      <span className="ct-program-header-type">{prog.programType}</span>
                    </span>
                    <span className="ct-contract-count">{progContracts.length}</span>
                  </button>
                  {expanded && (
                    <>
                      {progContracts.filter(c => searchQuery === '' || (c.name ?? '').toLowerCase().includes(searchQuery.toLowerCase())).map(c => (
                        <button
                          key={c.id}
                          className={`ct-contract-btn${editingId === c.id ? ' active' : ''}`}
                          onClick={() => startEdit(c)}
                        >
                          <span className="ct-contract-btn-inner">
                            <span className="ct-contract-name">{c.name || <em style={{ opacity: 0.6 }}>Untitled</em>}</span>
                            <span className="ct-contract-meta">
                              <span
                                className="ct-status-badge"
                                style={{ color: STATUS_COLORS[c.status] ?? '#6c757d' }}
                              >
                                {c.status}
                              </span>
                              <span style={{ opacity: 0.4 }}>{c.contractYear}</span>
                            </span>
                          </span>
                        </button>
                      ))}
                      <button className="ct-add-contract-btn" onClick={() => startNew(prog.id)}>
                        + Add Contract
                      </button>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* ── Main area + AI panel ── */}
        <PanelGroup direction="horizontal" style={{ flex: 1, minWidth: 0, background: 'var(--eh-bg-page)' }}>
          <Panel minSize={40} defaultSize={65} style={{ background: 'var(--eh-bg-page)' }}>
            <div className="overflow-auto p-4 h-100" style={{ background: 'var(--eh-bg-page)', overscrollBehaviorY: 'contain' }}>
              {loading ? (
                <div className="text-center py-5 text-muted">Loading…</div>
              ) : editingId === null ? (
                <div style={{ maxWidth: 860 }}>
                  {apiError && <div className="alert alert-danger py-2 mb-3">{apiError}</div>}
                  {statusFilter !== 'All' && (
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <span className="ct-status-pill" style={{ background: (STATUS_COLORS[statusFilter] ?? '#6c757d') + '22', color: STATUS_COLORS[statusFilter] ?? '#6c757d' }}>
                        {statusFilter}
                      </span>
                      <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                        Showing {filteredContracts.length} contract{filteredContracts.length !== 1 ? 's' : ''}
                      </span>
                      <button className="btn btn-sm btn-link p-0 ms-1 text-muted" style={{ fontSize: '0.8rem' }} onClick={() => setStatusFilter('All')}>Clear filter</button>
                    </div>
                  )}
                  {filteredContracts.length === 0 ? (
                    contracts.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem', opacity: 0.25 }}>&#9874;</div>
                      <p className="mb-1">No contracts configured yet.</p>
                      <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                        Expand a program in the sidebar and click <strong>+ Add Contract</strong>.
                      </p>
                    </div>
                    ) : (
                    <div className="text-center py-5 text-muted">
                      <p className="mb-1">No <strong>{statusFilter}</strong> contracts.</p>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setStatusFilter('All')}>Show all</button>
                    </div>
                    )
                  ) : (
                    filteredContracts.map(c => {
                      const prog = programs.find(p => p.id === c.programId);
                      return (
                        <div key={c.id} className="card ct-contract-card" onClick={() => startEdit(c)}>
                          <div className="card-body d-flex align-items-center justify-content-between">
                            <div>
                              <div className="fw-bold" style={{ color: 'var(--eh-navy)' }}>
                                {c.name || <em className="text-muted fw-normal">Untitled</em>}
                              </div>
                              <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                                {prog?.name ?? '—'} &middot; {c.contractYear}
                                {c.payer && <> &middot; {c.payer}</>}
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-3">
                              <span className="ct-status-pill" style={{ background: (STATUS_COLORS[c.status] ?? '#6c757d') + '22', color: STATUS_COLORS[c.status] ?? '#6c757d' }}>
                                {c.status}
                              </span>
                              <span style={{ color: 'var(--eh-teal)', fontSize: '1.3rem', lineHeight: 1 }}>›</span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <div style={{ maxWidth: 860 }}>
                  <h5 style={{ color: 'var(--eh-navy)', fontWeight: 700, marginBottom: '1.5rem' }}>
                    {editingId === 'new' ? 'New Contract' : 'Edit Contract'}
                  </h5>

                  {saved && (
                    <div className="alert alert-success py-2 mb-3">&#10003; Contract saved successfully.</div>
                  )}
                  {apiError && <div className="alert alert-danger py-2 mb-3">{apiError}</div>}

                  <form onSubmit={handleSave}>
                    {/* ── Contract Identity ── */}
                    <SectionHeading>Contract Identity</SectionHeading>
                    <div className="row g-3 mb-3">
                      <div className="col-md-5">
                        <Field label="Contract Name">
                          <input className="form-control" value={draft.name}
                            onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                            placeholder="e.g. 2026 MA BCBSNC TCOC Agreement" />
                        </Field>
                      </div>
                      <div className="col-md-3">
                        <Field label="Contract Year">
                          <select className="form-select" value={draft.contractYear}
                            onChange={e => setDraft(d => ({ ...d, contractYear: e.target.value }))}>
                            {['2024', '2025', '2026', '2027'].map(y => <option key={y}>{y}</option>)}
                          </select>
                        </Field>
                      </div>
                      <div className="col-md-4">
                        <Field label="Status">
                          <select className="form-select" value={draft.status}
                            onChange={e => setDraft(d => ({ ...d, status: e.target.value }))}>
                            <option>Draft</option>
                            <option>Active</option>
                            <option>Expired</option>
                            <option>Terminated</option>
                            <option>Pending Renewal</option>
                          </select>
                        </Field>
                      </div>
                    </div>
                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <Field label="Program">
                          <select className="form-select" value={draft.programId}
                            onChange={e => {
                              const prog = programs.find(p => p.id === e.target.value);
                              setDraft(d => ({ ...d, programId: e.target.value, _programType: prog?.programType ?? '' }));
                            }}>
                            <option value="">— Select program —</option>
                            {programs.map(p => (
                              <option key={p.id} value={p.id}>{p.name || '(Untitled)'} — {p.programType}</option>
                            ))}
                          </select>
                        </Field>
                      </div>
                      <div className="col-md-4">
                        <Field label="Contract Reference #">
                          <input className="form-control" value={draft.contractRef}
                            onChange={e => setDraft(d => ({ ...d, contractRef: e.target.value }))}
                            placeholder="e.g. VBC-2026-001" />
                        </Field>
                      </div>
                    </div>

                    {/* ── Parties ── */}
                    <SectionHeading>Contracting Parties</SectionHeading>
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <Field label="Payer / Health Plan">
                          <input className="form-control" value={draft.payer}
                            onChange={e => setDraft(d => ({ ...d, payer: e.target.value }))}
                            placeholder="e.g. BlueCross BlueShield of NC" />
                        </Field>
                      </div>
                      <div className="col-md-6">
                        <Field label="Provider Organization / Entity">
                          <input className="form-control" value={draft.providerOrg}
                            onChange={e => setDraft(d => ({ ...d, providerOrg: e.target.value }))}
                            placeholder="e.g. Piedmont Health ACO" />
                        </Field>
                      </div>
                    </div>

                    {/* ── Dates ── */}
                    <SectionHeading>Contract Period</SectionHeading>
                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <Field label="Effective Date">
                          <input className="form-control" type="date" value={draft.effectiveDate}
                            onChange={e => setDraft(d => ({ ...d, effectiveDate: e.target.value }))} />
                        </Field>
                      </div>
                      <div className="col-md-4">
                        <Field label="Termination Date">
                          <input className="form-control" type="date" value={draft.terminationDate}
                            onChange={e => setDraft(d => ({ ...d, terminationDate: e.target.value }))} />
                        </Field>
                      </div>
                      {draft._programType && (
                        <div className="col-md-4">
                          <Field label="Payment Model">
                            <input className="form-control" value={draft._programType} readOnly
                              style={{ background: 'var(--eh-bg-page)', color: 'var(--eh-text-mid)', cursor: 'default' }} />
                          </Field>
                        </div>
                      )}
                    </div>

                    {/* ── Type-specific fields ── */}
                    {TypeFields
                      ? <TypeFields form={draft} setForm={setDraft} />
                      : draft.programId
                        ? <p className="text-muted" style={{ fontSize: '0.9rem' }}>Select a program above to configure contract terms.</p>
                        : <p className="text-muted" style={{ fontSize: '0.9rem' }}>Select a program above to see contract-specific fields.</p>
                    }

                    <div className="mt-4 pt-3 border-top d-flex gap-2 align-items-center">
                      <button type="submit" className="btn btn-primary px-4 fw-bold"
                        disabled={!draft.programId || saving}>
                        {saving ? 'Saving…' : 'Save Contract'}
                      </button>
                      <button type="button" className="btn btn-outline-secondary px-4" onClick={cancel} disabled={saving}>
                        Cancel
                      </button>
                      {editingId !== 'new' && !deleteConfirm && (
                        <button type="button" className="btn btn-outline-danger ms-auto"
                          onClick={() => setDeleteConfirm(true)}>
                          Delete Contract
                        </button>
                      )}
                      {editingId !== 'new' && deleteConfirm && (
                        <div className="ms-auto d-flex align-items-center gap-2">
                          <span className="text-danger" style={{ fontSize: '0.9rem' }}>Delete this contract?</span>
                          <button type="button" className="btn btn-danger btn-sm" onClick={handleDelete}>Yes, delete</button>
                          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </div>
          </Panel>

          <PanelResizeHandle className="resize-handle">
            <div className="resize-handle-inner">
              <button
                className="collapse-btn"
                title={aiCollapsed ? 'Show AI assistant' : 'Hide AI assistant'}
                onClick={toggleAi}
                onPointerDown={e => e.stopPropagation()}
              >
                {aiCollapsed ? '◀' : '▶'}
              </button>
              <div className="resize-handle-bar" />
              <button
                className="collapse-btn"
                title={aiCollapsed ? 'Show AI assistant' : 'Hide AI assistant'}
                onClick={toggleAi}
                onPointerDown={e => e.stopPropagation()}
              >
                {aiCollapsed ? '◀' : '▶'}
              </button>
            </div>
          </PanelResizeHandle>

          <Panel
            ref={aiPanelRef}
            defaultSize={35}
            minSize={0}
            collapsible
            collapsedSize={0}
            onCollapse={() => setAiCollapsed(true)}
            onExpand={() => setAiCollapsed(false)}
          >
            <div className="h-100" style={{ borderLeft: '1px solid #dee2e6', background: 'var(--eh-bg-page)' }}>
              <ChatPanel
                activeTab="contracts"
                chatStateRef={chatStateRef}
                enableUpload
                onUpdateCurrent={(data) => {
                  const { typeData, ...baseFields } = data;
                  if (editingId !== null && draft) {
                    setDraft(d => ({ ...d, ...baseFields, ...(typeData || {}) }));
                  } else {
                    const programId = programs[0]?.id ?? '';
                    const prog = programs.find(p => p.id === programId);
                    setDraft({ programId, _programType: prog?.programType ?? '', name: '', contractYear: '2026', status: 'Draft', effectiveDate: '', terminationDate: '', payer: '', providerOrg: '', contractRef: '', ...baseFields, ...(typeData || {}) });
                    setEditingId('new');
                    setSaved(false);
                    setApiError(null);
                    setDeleteConfirm(false);
                    if (programId) setExpandedPrograms(prev => new Set([...prev, programId]));
                    history.pushState(null, '', '/contracts?id=new');
                  }
                }}
                onCreateNew={(data) => {
                  const { typeData, ...baseFields } = data;
                  const programId = draft?.programId ?? programs[0]?.id ?? '';
                  const prog = programs.find(p => p.id === programId);
                  setDraft({ programId, _programType: prog?.programType ?? '', name: '', contractYear: '2026', status: 'Draft', effectiveDate: '', terminationDate: '', payer: '', providerOrg: '', contractRef: '', ...baseFields, ...(typeData || {}) });
                  setEditingId('new');
                  setSaved(false);
                  setApiError(null);
                  setDeleteConfirm(false);
                  if (programId) setExpandedPrograms(prev => new Set([...prev, programId]));
                  history.pushState(null, '', '/contracts?id=new');
                }}
              />
            </div>
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
}
